import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, Sparkles, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, apiErr } from "../../lib/api";
import { COUNTRIES } from "../../lib/constants";
import Logo from "../../components/Logo";
import Seo from "../../components/Seo";

export default function AmbassadorInvite() {
  const { code: codeParam } = useParams();
  const { ambassadorRegister, login } = useAuth();
  const nav = useNavigate();

  const [code, setCode] = useState(codeParam ? codeParam.toUpperCase() : "");
  const [step, setStep] = useState(codeParam ? "checking" : "code"); // code | checking | invalid | register | login
  const [inviteName, setInviteName] = useState("");
  const [isGroup, setIsGroup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", country: "France", password: "", confirm: "" });
  const [login_, setLogin_] = useState({ email: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const validate = useCallback(async (c) => {
    const clean = (c || "").trim().toUpperCase();
    if (!clean) return toast.error("Veuillez saisir votre code d'invitation.");
    setLoading(true);
    try {
      const { data } = await api.get(`/ambassador/validate/${encodeURIComponent(clean)}`);
      if (!data.valid) { setStep("invalid"); return; }
      setInviteName(data.invite_name || "");
      setIsGroup(!!data.group);
      setStep(data.used ? "login" : "register");
    } catch {
      setStep("invalid");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (codeParam) validate(codeParam);
  }, [codeParam, validate]);

  const submitRegister = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Les mots de passe ne correspondent pas.");
    if (f.password.length < 6) return toast.error("Le mot de passe doit contenir au moins 6 caractères.");
    setLoading(true);
    try {
      const { confirm, ...rest } = f;
      await ambassadorRegister({ ...rest, code: code.trim().toUpperCase() });
      toast.success("Bienvenue au Comité des Ambassadeurs !");
      nav("/");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(login_.email, login_.password);
      toast.success("Content de vous revoir !");
      nav(u.role === "admin" ? "/admin" : u.role === "ambassador" ? "/ambassadeur/espace" : "/app");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="grain grid min-h-screen bg-void lg:grid-cols-2">
      <Seo title="Invitation Ambassadeur — Accès privé" description="Espace privé et exclusif réservé aux ambassadeurs académiques de APP CHALLENGE ACADEMY." path="/ambassadeur" noindex />

      {/* Panneau gauche — exclusivité */}
      <div className="relative hidden overflow-hidden border-r border-white/10 bg-surface lg:block">
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-cyan/20 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-24 -right-10 h-80 w-80 rounded-full bg-electric/20 blur-[120px]" />
        <img src="/aca-ecusson.png" alt="" className="pointer-events-none absolute bottom-6 right-6 w-[300px] max-w-none opacity-[0.08]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center justify-between gap-4">
            <Link to="/"><Logo /></Link>
            <img src="/aca-ecusson.png" alt="Écusson App Challenge Academy" data-testid="amb-ecusson" className="h-16 w-auto drop-shadow-[0_0_18px_rgba(0,240,255,0.35)]" />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-cyan">
              <Lock size={12} /> Invitation privée
            </div>
            <h2 className="mt-6 font-display text-4xl font-900 leading-tight text-white">
              Vous êtes convié au<br />Comité des Ambassadeurs Académiques
            </h2>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-ink-secondary">
              Un cercle confidentiel et privilégié. Découvrez l'application en avant-première,
              testez-la avant tout le monde et façonnez-la grâce à vos retours exclusifs.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-ink-secondary">
              <li className="flex items-center gap-3"><Sparkles size={16} className="text-cyan" /> Accès avant-première exclusif</li>
              <li className="flex items-center gap-3"><ShieldCheck size={16} className="text-cyan" /> Un code d'invitation unique et confidentiel</li>
              <li className="flex items-center gap-3"><KeyRound size={16} className="text-cyan" /> Un espace ambassadeur dédié et privé</li>
            </ul>
          </div>
          <div className="text-xs text-ink-tertiary">appchallengeacademy.com · Comité privé</div>
        </div>
      </div>

      {/* Panneau droit — action */}
      <div className="flex items-center justify-center overflow-y-auto px-5 py-16 sm:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white">
            <ArrowLeft size={15} /> Retour au site
          </Link>

          {/* Étape : saisie du code */}
          {(step === "code" || step === "checking" || step === "invalid") && (
            <div data-testid="ambassador-code-step">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-cyan lg:hidden">
                <Lock size={12} /> Invitation privée
              </div>
              <h1 className="mt-4 font-display text-3xl font-700 text-white lg:mt-0">Accès ambassadeur</h1>
              <p className="mt-2 text-sm text-ink-secondary">
                Saisissez le code d'invitation confidentiel que vous avez reçu pour accéder à l'avant-première.
              </p>
              <div className="mt-8 space-y-4">
                <label className="block">
                  <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Code d'invitation</span>
                  <input
                    value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="AMB-XXXXXX" data-testid="ambassador-code-input" autoFocus
                    onKeyDown={(e) => e.key === "Enter" && validate(code)}
                    className="w-full rounded-xl rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm uppercase tracking-widest text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
                  />
                </label>
                {step === "invalid" && (
                  <p className="text-sm text-red-400" data-testid="ambassador-invalid">
                    Ce code est invalide ou a été révoqué. Vérifiez votre invitation ou contactez l'Academy.
                  </p>
                )}
                <button
                  onClick={() => validate(code)} disabled={loading || step === "checking"} data-testid="ambassador-validate-btn"
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan px-6 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60"
                >
                  {loading || step === "checking" ? "Vérification…" : "Accéder à mon invitation"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          )}

          {/* Étape : inscription */}
          {step === "register" && (
            <div data-testid="ambassador-register-step">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-cyan">
                <Sparkles size={12} /> {isGroup ? "Programme Ambassadeurs" : "Invitation confirmée"}
              </div>
              <h1 className="mt-4 font-display text-3xl font-700 text-white">
                {isGroup ? "Rejoignez le Comité des Ambassadeurs" : (inviteName ? `Bienvenue, ${inviteName.split(" ")[0]}` : "Rejoignez le Comité")}
              </h1>
              <p className="mt-2 text-sm text-ink-secondary">
                {isGroup
                  ? "Créez votre accès ambassadeur : découvrez l’application en avant-première et aidez-nous à la faire connaître."
                  : "Créez votre accès ambassadeur pour découvrir l'application en avant-première."}
              </p>

              {isGroup && (
                <div className="mt-5 rounded-xl border border-cyan/20 bg-cyan/5 p-4" data-testid="amb-group-how">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-cyan">Comment ça marche</div>
                  <ol className="mt-3 space-y-2 text-sm text-ink-secondary">
                    <li className="flex gap-2"><span className="font-700 text-cyan">1.</span> Créez votre compte ambassadeur ci-dessous (gratuit, en 1 minute).</li>
                    <li className="flex gap-2"><span className="font-700 text-cyan">2.</span> Accédez à votre espace privé et découvrez l’application en avant-première.</li>
                    <li className="flex gap-2"><span className="font-700 text-cyan">3.</span> Partagez vos retours et faites connaître App Challenge Academy autour de vous.</li>
                  </ol>
                  <p className="mt-3 text-xs text-ink-tertiary">Ce lien est partagé avec votre groupe : chacun crée son propre compte avec ses informations.</p>
                </div>
              )}
              <div className="mt-4 flex items-center gap-3 rounded-xl border border-cyan/30 bg-cyan/10 px-4 py-3" data-testid="amb-prefilled-code">
                <KeyRound size={16} className="shrink-0 text-cyan" />
                <span className="text-sm text-ink-secondary">Votre code :</span>
                <span className="font-mono text-sm font-700 tracking-widest text-white">{code}</span>
                <ShieldCheck size={16} className="ml-auto shrink-0 text-cyan" />
              </div>
              {!isGroup && (
              <div className="mt-5 rounded-xl border border-cyan/20 bg-cyan/5 p-4 lg:hidden" data-testid="amb-why-invited">
                <div className="text-[11px] uppercase tracking-[0.15em] text-cyan">Pourquoi cette invitation ?</div>
                <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
                  Vous avez été personnellement invité(e) à rejoindre le cercle privé des ambassadeurs.
                  Votre rôle : découvrir l'application en avant-première, la tester et partager vos retours privilégiés.
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-ink-secondary">
                  <li className="flex items-center gap-2"><Sparkles size={14} className="text-cyan" /> Accès avant-première exclusif</li>
                  <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-cyan" /> Code personnel et confidentiel</li>
                  <li className="flex items-center gap-2"><KeyRound size={14} className="text-cyan" /> Espace ambassadeur privé</li>
                </ul>
              </div>
              )}
              <form onSubmit={submitRegister} className="mt-8 space-y-4" data-testid="ambassador-register-form">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Prénom" value={f.first_name} onChange={set("first_name")} testid="amb-firstname" autoFocus />
                  <Field label="Nom" value={f.last_name} onChange={set("last_name")} testid="amb-lastname" />
                </div>
                <Field label="E-mail" type="email" value={f.email} onChange={set("email")} testid="amb-email" />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Téléphone" value={f.phone} onChange={set("phone")} required={false} testid="amb-phone" />
                  <label className="block">
                    <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Pays</span>
                    <select value={f.country} onChange={set("country")} data-testid="amb-country" className="w-full rounded-xl rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan">
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                </div>
                <Field label="Mot de passe" type="password" value={f.password} onChange={set("password")} testid="amb-password" />
                <Field label="Confirmer le mot de passe" type="password" value={f.confirm} onChange={set("confirm")} testid="amb-confirm" />
                <button
                  type="submit" disabled={loading} data-testid="ambassador-register-submit"
                  className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan px-6 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60"
                >
                  {loading ? "Création…" : "Rejoindre le Comité"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
              <button onClick={() => setStep("login")} className="mt-6 text-sm text-ink-secondary hover:text-cyan" data-testid="amb-to-login">
                Vous avez déjà un compte ambassadeur ? Se connecter
              </button>
            </div>
          )}

          {/* Étape : connexion */}
          {step === "login" && (
            <div data-testid="ambassador-login-step">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-cyan">
                <Lock size={12} /> Espace privé
              </div>
              <h1 className="mt-4 font-display text-3xl font-700 text-white">Connexion ambassadeur</h1>
              <p className="mt-2 text-sm text-ink-secondary">Connectez-vous à votre espace ambassadeur privé.</p>
              <form onSubmit={submitLogin} className="mt-8 space-y-4" data-testid="ambassador-login-form">
                <Field label="E-mail" type="email" value={login_.email} onChange={(e) => setLogin_({ ...login_, email: e.target.value })} testid="amb-login-email" autoFocus />
                <Field label="Mot de passe" type="password" value={login_.password} onChange={(e) => setLogin_({ ...login_, password: e.target.value })} testid="amb-login-password" />
                <button
                  type="submit" disabled={loading} data-testid="ambassador-login-submit"
                  className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan px-6 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60"
                >
                  {loading ? "Connexion…" : "Entrer dans l'espace"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
              <button onClick={() => setStep("code")} className="mt-6 text-sm text-ink-secondary hover:text-cyan" data-testid="amb-to-code">
                Utiliser un autre code d'invitation
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required = true, autoFocus }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{label}</span>
      <input
        required={required} type={type} value={value} onChange={onChange} data-testid={testid} autoFocus={autoFocus}
        className="w-full rounded-xl rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
      />
    </label>
  );
}
